import { Download, Paperclip, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ApiError } from '../../../../lib/apiError';
import { purchaseRequestsApi } from '../purchaseRequestsApi';
import type { PurchaseRequestDetail } from '../purchaseRequestTypes';
import { AttachQuotationModal } from './AttachQuotationModal';

/**
 * The request's Sales Quotation — filed, downloaded, removed.
 *
 * **It exists because the three endpoints behind it had no control anywhere.** The attach, the
 * download and the remove all shipped gated and unreachable, which is non-negotiable 2's own
 * shape one level down: for each endpoint, name the control that reaches it. The source's own
 * Add modal carries a SALES QUOTATION section, so the create form asks for one too — this card
 * is how it is corrected afterwards.
 *
 * **Its own file**, like every other panel: the profile page is long enough already, and a new
 * file cannot collide with a second session editing the same feature.
 */
export function QuotationCard({
  request,
  onChanged,
}: {
  request: PurchaseRequestDetail;
  /** Handed the refreshed record, so the card and the header cannot disagree. */
  onChanged: (next: PurchaseRequestDetail) => void;
}) {
  const [attaching, setAttaching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    `!= null`, NEVER `!== null` — and this is the defect that shipped for an hour rather than a
    style choice. The API is configured `DefaultIgnoreCondition = WhenWritingNull`, so a null
    `Guid?` is OMITTED from the JSON entirely and arrives as `undefined`. Compared strictly,
    `undefined !== null` is TRUE, so this card stated "One quotation is filed against this
    request" on every request that had none — drew no empty state, and offered a Download of a
    document that does not exist.

    TYPESCRIPT CANNOT SEE IT: `purchaseRequestTypes.ts` declares the field `string | null`,
    which is what the DTO promises and not what the wire sends. Only opening the screen found
    it, which is the Stock Levels investment filter's own lesson one suite along.
  */
  const filed = request.quotationRepositoryItemId != null;

  /*
    A FETCH, then an object URL, then a click — never an `<a href>` at the endpoint. It carries
    `purchase-requests.read`, the token travels in a header no navigation sends, and the API has
    its own origin. The document profile's own download takes exactly this shape.
  */
  async function download() {
    setBusy(true);
    setError(null);

    try {
      const { blob, fileName } = await purchaseRequestsApi.downloadQuotation(request.id);
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');

      anchor.href = url;
      anchor.download = fileName ?? `${request.requestNumber}-quotation`;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not download the quotation.', 0),
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);

    try {
      onChanged(await purchaseRequestsApi.removeQuotation(request.id));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not remove the quotation.', 0),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ProfileCard
        id="purchase-request-profile-quotation"
        title="Sales quotation"
        /*
          AN EMPTY CARD SAYS SO AND OFFERS THE WAY OUT, which is the card's own rule: a bare
          sentence naming the cog in prose is the tell that the affordance belongs in the banner.
          The banner's button IS `onEdit`, so it files the first one.
        */
        empty={
          filed
            ? undefined
            : {
                title: 'No quotation filed',
                description:
                  "A supplier's quotation is what the estimated figures on this request are read against. File one when it arrives.",
              }
        }
        onEdit={request.isEditable ? () => setAttaching(true) : undefined}
        editPermission="purchase-requests.update"
        editLabel={filed ? 'Replace it' : 'File a quotation'}
        editDisabledReason={
          request.isEditable ? null : 'Only a draft can have its quotation changed.'
        }
        menuItems={
          filed
            ? [
                {
                  key: 'download-quotation',
                  label: 'Download',
                  icon: <Download className="h-4 w-4" aria-hidden="true" />,
                  permission: 'purchase-requests.read',
                  disabledReason: busy ? 'Working…' : null,
                  onClick: () => void download(),
                },
                {
                  key: 'remove-quotation',
                  label: 'Remove',
                  icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
                  permission: 'purchase-requests.update',
                  disabledReason: request.isEditable
                    ? busy
                      ? 'Working…'
                      : null
                    : 'Only a draft can have its quotation removed.',
                  onClick: () => void remove(),
                },
              ]
            : undefined
        }
      >
        <div className="flex flex-col gap-2">
          {error ? (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
            >
              {error.message}
            </p>
          ) : null}

          {filed ? (
            <PreviewTable
              variant="table-display"
              rows={[
                {
                  label: 'Filed',
                  value: (
                    <span className="inline-flex items-center gap-1.5">
                      <Paperclip className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                      One quotation is filed against this request
                    </span>
                  ),
                },
                /*
                  THE REPOSITORY ITEM'S ID, and not its NAME — the detail DTO carries the id and
                  nothing else, so a name here would be a value the record has no way to fill.
                  Naming the document is the Repository's own answer, one link away.
                */
                { label: 'Repository item', value: request.quotationRepositoryItemId, mono: true },
              ]}
            />
          ) : null}
        </div>
      </ProfileCard>

      {attaching ? (
        <AttachQuotationModal
          open
          request={request}
          onClose={() => setAttaching(false)}
          onAttached={(next) => {
            setAttaching(false);
            onChanged(next);
          }}
        />
      ) : null}
    </>
  );
}
